import { memo } from "react";
import { Handle, Position } from "@xyflow/react";
import { theme } from "antd";
import {
  TeamOutlined,
  ApartmentOutlined,
} from "@ant-design/icons";
import type { TreeNodeData } from "./useTreeLayout";

interface DepartmentNodeProps {
  data: TreeNodeData;
}

/**
 * Custom React Flow node for department org chart.
 * Shows department name, code, legal entity, and child count.
 */
export const DepartmentNode = memo(({ data }: DepartmentNodeProps) => {
  const { token } = theme.useToken();
  const isRoot = data.depth === 0;
  const hasChildren = data.childCount > 0;

  const bgColor = isRoot ? token.colorPrimaryBg : token.colorBgContainer;
  const borderColor = isRoot ? token.colorPrimary : token.colorBorderSecondary;
  const headerColor = isRoot ? token.colorPrimary : token.colorText;

  return (
    <div
      style={{
        background: bgColor,
        border: `2px solid ${borderColor}`,
        borderRadius: 10,
        padding: "10px 14px",
        minWidth: 200,
        maxWidth: 220,
        boxShadow: isRoot
          ? `0 4px 12px ${token.colorPrimaryBg}`
          : `0 2px 8px rgba(0,0,0,0.06)`,
        cursor: "pointer",
        transition: "box-shadow 0.2s, border-color 0.2s",
      }}
    >
      {/* Department name */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 4,
        }}
      >
        {isRoot ? (
          <ApartmentOutlined style={{ color: headerColor, fontSize: 14 }} />
        ) : (
          <TeamOutlined style={{ color: token.colorTextTertiary, fontSize: 13 }} />
        )}
        <span
          style={{
            fontWeight: 600,
            fontSize: 13,
            color: headerColor,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
          title={data.label}
        >
          {data.label}
        </span>
      </div>

      {/* Code + Legal Entity */}
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {data.code && (
          <span
            style={{
              fontSize: 11,
              color: token.colorTextSecondary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {data.code}
          </span>
        )}
        {data.legalEntity && (
          <span
            style={{
              fontSize: 11,
              color: token.colorTextTertiary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={data.legalEntity}
          >
            {data.legalEntity}
          </span>
        )}
      </div>

      {/* Child count badge */}
      {hasChildren && (
        <div
          style={{
            position: "absolute",
            top: -8,
            right: -8,
            background: token.colorPrimary,
            color: "#fff",
            borderRadius: "50%",
            width: 20,
            height: 20,
            fontSize: 10,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {data.childCount}
        </div>
      )}

      {/* Handles for edges */}
      {data.depth > 0 && (
        <Handle
          type="target"
          position={Position.Top}
          style={{
            background: borderColor,
            width: 8,
            height: 8,
            border: "none",
            top: -4,
          }}
        />
      )}
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: borderColor,
          width: 8,
          height: 8,
          border: "none",
          bottom: -4,
        }}
      />
    </div>
  );
});

DepartmentNode.displayName = "DepartmentNode";
